import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Panel } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const Row = ({ label, value, testid }) => (
  <div className="py-3 border-b last:border-0 grid grid-cols-[150px_1fr] gap-4">
    <dt className="text-xs font-mono-x uppercase tracking-wider text-muted-foreground pt-0.5">{label}</dt>
    <dd className="font-medium break-words" data-testid={testid}>{value || "—"}</dd>
  </div>
);

const FivEditor = () => {
  const { user, setUser } = useAuth();
  const [val, setVal] = useState(user.tessera_fiv || "");
  const save = async () => {
    try {
      const { data } = await api.put("/profile/tessera-fiv", { tessera_fiv: val });
      setUser(data);
      toast.success("Numero tessera FIV aggiornato");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-6">
      <Label>Numero tessera FIV</Label>
      <div className="flex gap-2 mt-1">
        <Input value={val} onChange={(e) => setVal(e.target.value)} data-testid="profile-fiv-input" />
        <Button onClick={save} disabled={!val.trim() || val === user.tessera_fiv} className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-fiv-save-button">Salva</Button>
      </div>
    </div>
  );
};

const EmailPref = () => {
  const { user, setUser } = useAuth();
  const change = async (v) => {
    try {
      const { data } = await api.put("/profile/preferenze", { notify_email_comunicazioni: v });
      setUser(data);
      toast.success(v ? "Avvisi email attivati" : "Avvisi email disattivati");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <label className="flex items-start gap-3 mt-6 pt-6 border-t cursor-pointer">
      <Switch checked={!!user.notify_email_comunicazioni} onCheckedChange={change} data-testid="profile-email-pref-switch" />
      <span className="text-sm">
        <span className="font-medium block">Avvisami via email delle nuove comunicazioni</span>
        <span className="text-muted-foreground">Ricevi un'email quando la segreteria pubblica una comunicazione per te.</span>
      </span>
    </label>
  );
};

const ContattiEditor = () => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ indirizzo: user.indirizzo || "", telefono: user.telefono || "", email: user.email || "", luogo_nascita: user.luogo_nascita || "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/profile/contatti", f);
      setUser(data.user);
      toast.success(data.message);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={save} className="mt-6 pt-6 border-t space-y-3" data-testid="contatti-form">
      <div className="font-heading text-lg uppercase">Modifica recapiti</div>
      <div><Label>Luogo di nascita</Label><Input required maxLength={100} value={f.luogo_nascita} onChange={set("luogo_nascita")} data-testid="contatti-luogo-nascita-input" /></div>
      <div><Label>Indirizzo</Label><Input required value={f.indirizzo} onChange={set("indirizzo")} data-testid="contatti-indirizzo-input" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Cellulare</Label><Input required value={f.telefono} onChange={set("telefono")} data-testid="contatti-telefono-input" /></div>
        <div><Label>Email</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="contatti-email-input" /></div>
      </div>
      {user.pending_email && <p className="text-xs text-amber-700" data-testid="contatti-pending-email">In attesa di conferma: {user.pending_email}</p>}
      <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="contatti-save-button">Salva recapiti</Button>
    </form>
  );
};

const DobEditor = () => {
  const { setUser } = useAuth();
  const [val, setVal] = useState("");
  const save = async () => {
    if (!window.confirm("La data di nascita può essere impostata una sola volta. Confermi?")) return;
    try {
      const { data } = await api.put("/profile/data-nascita", { data_nascita: val });
      setUser(data);
      toast.success("Data di nascita salvata");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="mt-6 pt-6 border-t" data-testid="dob-editor">
      <Label>Data di nascita (modificabile una sola volta)</Label>
      <div className="flex gap-2 mt-1">
        <Input type="date" value={val} onChange={(e) => setVal(e.target.value)} data-testid="dob-input" />
        <Button onClick={save} disabled={!val} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dob-save-button">Salva</Button>
      </div>
    </div>
  );
};

const PasswordForm = () => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Le nuove password non coincidono");
    try {
      await api.put("/profile/password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password modificata");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="password-form">
      <div><Label>Password attuale</Label><Input type="password" required value={f.current_password} onChange={set("current_password")} data-testid="password-current-input" /></div>
      <div><Label>Nuova password (min. 8)</Label><Input type="password" required minLength={8} value={f.new_password} onChange={set("new_password")} data-testid="password-new-input" /></div>
      <div><Label>Conferma nuova password</Label><Input type="password" required minLength={8} value={f.confirm} onChange={set("confirm")} data-testid="password-confirm-input" /></div>
      <Button type="submit" className="bg-[#0B192C] hover:bg-[#1E3E62]" data-testid="password-submit-button">Modifica password</Button>
    </form>
  );
};

export default function Profile() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader kicker="Il tuo account" title="Profilo" />
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <Panel className="fade-up" data-testid="profile-data-panel">
          <h2 className="font-heading text-2xl uppercase mb-4">Dati anagrafici</h2>
          <dl>
            <Row label="Nome" value={user.nome} testid="profile-nome" />
            <Row label="Cognome" value={user.cognome} testid="profile-cognome" />
            <Row label="Data di nascita" value={fmtDate(user.data_nascita)} testid="profile-dob" />
            <Row label="Luogo di nascita" value={user.luogo_nascita || "—"} testid="profile-luogo-nascita" />
            <Row label="Genere" value={user.genere} testid="profile-genere" />
            <Row label="Email" value={user.email} testid="profile-email" />
            <Row label="Indirizzo" value={user.indirizzo} testid="profile-indirizzo" />
            <Row label="Telefono" value={user.telefono} testid="profile-telefono" />
            <Row label="Login" value={user.username} testid="profile-username" />
            <Row label="N. tessera socio" value={user.tessera_numero} testid="profile-tessera-numero" />
          </dl>
          <FivEditor />
          <ContattiEditor />
          {!user.data_nascita && <DobEditor />}
          <EmailPref />
        </Panel>
        <Panel className="fade-up h-fit">
          <h2 className="font-heading text-2xl uppercase mb-4">Cambia password</h2>
          <PasswordForm />
        </Panel>
      </div>
    </div>
  );
}
